import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Edit2, Trash2, Loader2, Save, X, Search,
  Package, DollarSign, Check, Image as ImageIcon
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Injectables Package',
  'Skincare Package',
  'Laser Package',
  'Wellness Package',
  'Facial Package',
  'Body Package',
  'Custom Package'
];

const AdminPackages = () => {
  const { toast } = useToast();
  const [packages, setPackages] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Injectables Package',
    price: '',
    originalPrice: '',
    description: '',
    includedTreatments: '',
    sessions: '',
    validityDays: '365',
    image: '',
    popular: false,
    active: true
  });

  useEffect(() => {
    fetchPackages();
    fetchTreatments();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/packages?practiceId=demo-practice-001`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTreatments = async () => {
    try {
      const response = await fetch(`${API}/treatments?practiceId=demo-practice-001`);
      if (response.ok) {
        const data = await response.json();
        setTreatments(data);
      }
    } catch (err) {
      console.error('Error fetching treatments:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const packageData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        originalPrice: parseFloat(formData.originalPrice) || 0,
        sessions: parseInt(formData.sessions) || 1,
        validityDays: parseInt(formData.validityDays) || 365,
        includedTreatments: formData.includedTreatments.split('\n').map(t => t.trim()).filter(Boolean),
        practiceId: 'demo-practice-001'
      };

      let response;
      if (editingPackage) {
        response = await fetch(`${API}/packages/${editingPackage.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(packageData)
        });
      } else {
        response = await fetch(`${API}/packages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(packageData)
        });
      }

      if (!response.ok) throw new Error('Failed to save package');

      toast({
        title: editingPackage ? 'Package Updated' : 'Package Added',
        description: `${formData.name} has been ${editingPackage ? 'updated' : 'added'} successfully.`
      });

      resetForm();
      fetchPackages();
    } catch (err) {
      console.error('Error saving package:', err);
      toast({
        title: 'Error',
        description: 'Failed to save package. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name || '',
      category: pkg.category || 'Injectables Package',
      price: pkg.price?.toString() || '',
      originalPrice: pkg.originalPrice?.toString() || '',
      description: pkg.description || '',
      includedTreatments: (pkg.includedTreatments || []).join('\n'),
      sessions: pkg.sessions?.toString() || '',
      validityDays: pkg.validityDays?.toString() || '365',
      image: pkg.image || '',
      popular: pkg.popular || false,
      active: pkg.active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Are you sure you want to delete "${pkg.name}"?`)) return;

    try {
      const response = await fetch(`${API}/packages/${pkg.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete package');

      toast({
        title: 'Package Deleted',
        description: `${pkg.name} has been deleted.`
      });

      fetchPackages();
    } catch (err) {
      console.error('Error deleting package:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete package.',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Injectables Package',
      price: '',
      originalPrice: '',
      description: '',
      includedTreatments: '',
      sessions: '',
      validityDays: '365',
      image: '',
      popular: false,
      active: true
    });
    setEditingPackage(null);
    setShowForm(false);
  };

  const calculateSavings = (original, current) => {
    if (!original || !current || original <= current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading packages...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Treatment Packages</h1>
          <p className="text-gray-600 mt-2">Create bundled packages for your clients at discounted prices</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Package
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Packages</p>
                <p className="text-2xl font-bold">{packages.length}</p>
              </div>
              <Package className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Packages</p>
                <p className="text-2xl font-bold">{packages.filter(p => p.active).length}</p>
              </div>
              <Check className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg. Package Price</p>
                <p className="text-2xl font-bold">
                  ${packages.length > 0 ? Math.round(packages.reduce((sum, p) => sum + (p.price || 0), 0) / packages.length) : 0}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Popular Packages</p>
                <p className="text-2xl font-bold">{packages.filter(p => p.popular).length}</p>
              </div>
              <Package className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Package Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingPackage ? 'Edit Package' : 'Add New Package'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Create a bundle of treatments at a special price
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Package Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Injectable Refresh Package"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="price">Package Price *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="price"
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        placeholder="999"
                        required
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="originalPrice">Original Value</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="originalPrice"
                        type="number"
                        value={formData.originalPrice}
                        onChange={(e) => setFormData({...formData, originalPrice: e.target.value})}
                        placeholder="1200"
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Shows savings to clients</p>
                  </div>
                  <div>
                    <Label htmlFor="sessions">Number of Sessions</Label>
                    <Input
                      id="sessions"
                      type="number"
                      value={formData.sessions}
                      onChange={(e) => setFormData({...formData, sessions: e.target.value})}
                      placeholder="3"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe what's included and the benefits..."
                    rows={2}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="includedTreatments">Included Treatments (one per line)</Label>
                  <Textarea
                    id="includedTreatments"
                    value={formData.includedTreatments}
                    onChange={(e) => setFormData({...formData, includedTreatments: e.target.value})}
                    placeholder="Botox (20 units)&#10;Lip Filler (1 syringe)&#10;Chemical Peel"
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="validityDays">Valid for (days)</Label>
                    <Input
                      id="validityDays"
                      type="number"
                      value={formData.validityDays}
                      onChange={(e) => setFormData({...formData, validityDays: e.target.value})}
                      placeholder="365"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="image">Image URL</Label>
                    <Input
                      id="image"
                      value={formData.image}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.popular}
                      onChange={(e) => setFormData({...formData, popular: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Mark as Popular</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {editingPackage ? 'Update Package' : 'Add Package'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Packages List */}
      {packages.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No packages yet</h3>
            <p className="text-gray-500 mb-4">Create bundled treatment packages to offer your clients</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Package
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <Card key={pkg.id} className={`relative ${pkg.popular ? 'ring-2 ring-primary' : ''}`}>
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary">Best Value</Badge>
                </div>
              )}
              {pkg.image && (
                <div className="h-40 overflow-hidden rounded-t-lg">
                  <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
                </div>
              )}
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <Badge variant="secondary" className="mb-2">{pkg.category}</Badge>
                    <CardTitle className="flex items-center gap-2">
                      {pkg.name}
                      {!pkg.active && <Badge variant="outline">Inactive</Badge>}
                    </CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(pkg)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">${pkg.price}</span>
                    {pkg.originalPrice > pkg.price && (
                      <>
                        <span className="text-lg text-gray-400 line-through">${pkg.originalPrice}</span>
                        <Badge className="bg-green-100 text-green-800">
                          Save {calculateSavings(pkg.originalPrice, pkg.price)}%
                        </Badge>
                      </>
                    )}
                  </div>
                  {pkg.sessions && <p className="text-sm text-gray-500">{pkg.sessions} sessions included</p>}
                </div>
                
                {pkg.description && (
                  <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
                )}

                {pkg.includedTreatments && pkg.includedTreatments.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Includes:</p>
                    <ul className="space-y-1">
                      {pkg.includedTreatments.slice(0, 4).map((treatment, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                          <Check className="h-3 w-3 text-green-500" />
                          {treatment}
                        </li>
                      ))}
                      {pkg.includedTreatments.length > 4 && (
                        <li className="text-sm text-gray-500">+{pkg.includedTreatments.length - 4} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {pkg.validityDays && (
                  <p className="text-xs text-gray-400 mt-4">Valid for {pkg.validityDays} days from purchase</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
